import React from 'react';
import PropTypes from 'prop-types';
import { FaFolder, FaEllipsisV, FaEdit, FaTrash } from 'react-icons/fa';

const CLASS_OPTIONS = [
  { value: 'class6', display: 'Class 6th' },
  { value: 'class9', display: 'Class 9th' }
];

const FolderList = ({
  folders,
  showActions,
  activeActionMenu,
  onFolderClick,
  onToggleActionMenu,
  onEditFolder,
  onDeleteFolder
}) => {
  const styles = {
    listContainer: {
      backgroundColor: 'white',
      borderRadius: '0.5rem',
      border: '1px solid #E5E7EB',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      marginBottom: '2rem'
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '0.625rem 1rem',
      backgroundColor: '#F9FAFB',
      borderBottom: '1px solid #E5E7EB',
      borderTopLeftRadius: '0.5rem',
      borderTopRightRadius: '0.5rem',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: '#6B7280',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #F3F4F6',
      transition: 'background-color 0.2s',
      cursor: 'pointer'
    },
    nameCell: {
      display: 'flex',
      alignItems: 'center',
      flex: '1 1 auto',
      minWidth: 0
    },
    folderIcon: {
      color: '#F59E0B',
      marginRight: '0.75rem',
      fontSize: '1.125rem',
      flexShrink: 0
    },
    folderName: {
      fontWeight: '500',
      color: '#1F2937',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    metaCell: {
      flex: '0 0 120px',
      fontSize: '0.875rem',
      color: '#4B5563',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    dateCell: {
      flex: '0 0 100px',
      fontSize: '0.875rem',
      color: '#6B7280'
    },
    actionsCell: {
      flex: '0 0 40px',
      display: 'flex',
      justifyContent: 'flex-end',
      position: 'relative'
    },
    actionButton: {
      color: '#6B7280',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    actionMenu: {
      position: 'absolute',
      top: '100%',
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #E5E7EB',
      borderRadius: '0.5rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      zIndex: 10,
      minWidth: '120px',
      overflow: 'hidden'
    },
    actionMenuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      color: '#374151',
      fontSize: '0.875rem',
      width: '100%',
      textAlign: 'left',
      border: 'none',
      background: 'none'
    },
    deleteMenuItem: {
      color: '#EF4444'
    },
    empty: {
      color: '#D1D5DB'
    }
  };

  const renderMeta = (value) => value || <span style={styles.empty}>—</span>;

  return (
    <div style={styles.listContainer}>
      <div style={styles.headerRow}>
        <div style={styles.nameCell}>Name</div>
        <div style={styles.metaCell}>Class</div>
        <div style={styles.metaCell}>Subject</div>
        <div style={styles.metaCell}>Target</div>
        <div style={styles.dateCell}>Updated</div>
        {showActions && <div style={styles.actionsCell} />}
      </div>

      {folders.map(folder => (
        <div
          key={folder.id}
          style={styles.row}
          onClick={() => onFolderClick(folder)}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <div style={styles.nameCell} title={folder.name}>
            <FaFolder style={styles.folderIcon} />
            <span style={styles.folderName}>{folder.name}</span>
          </div>
          <div style={styles.metaCell}>
            {renderMeta(folder.class && (CLASS_OPTIONS.find(option => option.value === folder.class)?.display || folder.class))}
          </div>
          <div style={styles.metaCell}>{renderMeta(folder.subject)}</div>
          <div style={styles.metaCell} title={folder.course_target || ''}>{renderMeta(folder.course_target)}</div>
          <div style={styles.dateCell}>
            {new Date(folder.updated_at).toLocaleDateString()}
          </div>

          {showActions && (
            <div
              className="action-menu-container"
              style={styles.actionsCell}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => onToggleActionMenu(folder.id)}
                style={styles.actionButton}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <FaEllipsisV />
              </button>

              {activeActionMenu === folder.id && (
                <div style={styles.actionMenu}>
                  <button
                    type="button"
                    style={styles.actionMenuItem}
                    onClick={() => onEditFolder(folder)}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    type="button"
                    style={{...styles.actionMenuItem, ...styles.deleteMenuItem}}
                    onClick={() => onDeleteFolder(folder)}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

FolderList.propTypes = {
  folders: PropTypes.arrayOf(PropTypes.object).isRequired,
  showActions: PropTypes.bool.isRequired,
  activeActionMenu: PropTypes.number,
  onFolderClick: PropTypes.func.isRequired,
  onToggleActionMenu: PropTypes.func,
  onEditFolder: PropTypes.func,
  onDeleteFolder: PropTypes.func
};

export default FolderList;
